import { useEffect, useState, type FormEvent } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import type { Evento } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Eyebrow } from '../components/Eyebrow';
import { CargaPagina } from '../components/Spinner';
import { TextField } from '../components/TextField';
import { db } from '../lib/firebase';
import { apiAuth, ApiError } from '../lib/apiAuth';

interface EventoConId extends Evento {
  eventoId: string;
}

const ETIQUETA_ESTADO: Record<Evento['estado'], string> = {
  borrador: 'Borrador',
  activo: 'Activo',
  archivado: 'Archivado',
};

export function EventosListaScreen() {
  const [eventos, setEventos] = useState<EventoConId[] | null>(null);
  const [formAbierto, setFormAbierto] = useState(false);

  useEffect(
    () =>
      onSnapshot(query(collection(db, 'eventos'), orderBy('creadoEn', 'desc')), (s) =>
        setEventos(s.docs.map((d) => ({ eventoId: d.id, ...(d.data() as Evento) }))),
      ),
    [],
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow>Administración</Eyebrow>
          <h1 className="mt-1 text-xl font-semibold text-texto">Eventos</h1>
        </div>
        <Button onClick={() => setFormAbierto((v) => !v)}>
          {formAbierto ? 'Cancelar' : 'Nuevo evento'}
        </Button>
      </div>

      {formAbierto && (
        <FormularioEvento onCreado={() => setFormAbierto(false)} />
      )}

      {eventos === null ? (
        <CargaPagina texto="Cargando eventos" />
      ) : eventos.length === 0 ? (
        <Card className="p-6 text-center text-sm text-texto/60">
          Todavía no hay eventos. Crea el primero arriba.
        </Card>
      ) : (
        <ul className="space-y-3">
          {eventos.map((e) => (
            <li key={e.eventoId}>
              <Link to={`/admin/eventos/${e.eventoId}`}>
                <Card className="flex items-center justify-between gap-3 p-4 transition hover:border-navy/30">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-texto">{e.nombre}</p>
                    <p className="truncate text-sm text-texto/60">
                      {e.fechaTexto} · {e.lugarTexto}
                    </p>
                  </div>
                  <span
                    className={[
                      'shrink-0 rounded-full px-3 py-1 text-xs font-medium',
                      e.estado === 'activo'
                        ? 'bg-emerald-50 text-emerald-700'
                        : e.estado === 'archivado'
                          ? 'bg-texto/5 text-texto/50'
                          : 'bg-oro/10 text-[#7a5a13]',
                    ].join(' ')}
                  >
                    {ETIQUETA_ESTADO[e.estado]}
                  </span>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormularioEvento({ onCreado }: { onCreado: () => void }) {
  const [nombre, setNombre] = useState('');
  const [fechaTexto, setFechaTexto] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [lugarTexto, setLugarTexto] = useState('');
  const [aforoObjetivo, setAforoObjetivo] = useState('100');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await apiAuth('/admin/eventos', {
        method: 'POST',
        body: JSON.stringify({
          nombre,
          fechaTexto,
          fechaInicio: fechaInicio || null,
          lugarTexto,
          mapsUrl: '',
          aforoObjetivo: Number(aforoObjetivo) || 0,
          ventanaRegistro: { abre: null, cierra: null },
        }),
      });
      onCreado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo crear el evento');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card className="p-5">
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <TextField label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Fecha (texto que ve el invitado)"
            placeholder="10 de octubre"
            value={fechaTexto}
            onChange={(e) => setFechaTexto(e.target.value)}
            required
          />
          <TextField
            label="Fecha de inicio"
            type="date"
            hint="Para ordenar la lista, opcional"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>
        <TextField label="Lugar" value={lugarTexto} onChange={(e) => setLugarTexto(e.target.value)} required />
        <TextField
          label="Objetivo de aforo"
          type="number"
          inputMode="numeric"
          min={0}
          value={aforoObjetivo}
          onChange={(e) => setAforoObjetivo(e.target.value)}
        />
        {error && <Callout tono="error">{error}</Callout>}
        <Button type="submit" cargando={enviando}>
          Crear evento
        </Button>
      </form>
    </Card>
  );
}
